import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';

function AdminManagement() {
  const [user] = useAuthState(auth);
  const [admins, setAdmins] = useState([]);
  const [clubs, setClubs] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [filter, setFilter] = useState('all'); // all, super_admin, club_admin

  const checkAccessAndLoadData = useCallback(async () => {
    if (!user) return;

    try {
      // Check if current user is super admin
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists() || userDoc.data().role !== 'super_admin') {
        setError('Access denied. Super Admin only.');
        setLoading(false);
        return;
      }
      setIsSuperAdmin(true);

      // Load all clubs for reference
      const clubsSnap = await getDocs(collection(db, 'clubs'));
      const clubsMap = {};
      clubsSnap.forEach(doc => {
        clubsMap[doc.id] = doc.data();
      });
      setClubs(clubsMap);

      // Load all users
      await loadUsers(clubsMap);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data: ' + err.message);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    checkAccessAndLoadData();
  }, [checkAccessAndLoadData]);

  const loadUsers = async (clubsMap) => {
    // Get all users with admin roles
    const adminsQuery = query(
      collection(db, 'users'),
      where('role', 'in', ['super_admin', 'club_admin'])
    );
    const adminsSnap = await getDocs(adminsQuery);
    const adminsList = [];
    adminsSnap.forEach(doc => {
      adminsList.push({ id: doc.id, ...doc.data() });
    });
    setAdmins(adminsList);

    // Get all users for promotion
    const allUsersSnap = await getDocs(collection(db, 'users'));
    const usersList = [];
    allUsersSnap.forEach(doc => {
      usersList.push({ id: doc.id, ...doc.data() });
    });
    setAllUsers(usersList);
  };

  const updateUserRole = async (userId, newRole) => {
    if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: new Date(),
        updatedBy: user.uid
      });
      
      setSuccess(`User role updated to ${newRole}`);
      
      // Reload users
      await loadUsers(clubs);
    } catch (err) {
      console.error('Error updating role:', err);
      setError('Failed to update role: ' + err.message);
    }
  };

  const assignToClub = async (userId, clubId) => {
    setError('');
    setSuccess('');

    try {
      await updateDoc(doc(db, 'users', userId), {
        clubId: clubId || null,
        updatedAt: new Date(),
        updatedBy: user.uid
      });
      
      setSuccess(`User ${clubId ? 'assigned to ' + clubs[clubId]?.name : 'unassigned from club'}`);
      
      // Reload users
      await loadUsers(clubs);
    } catch (err) {
      console.error('Error assigning club:', err);
      setError('Failed to assign club: ' + err.message);
    }
  };

  const getDisplayName = (userData) => {
    if (userData.firstName && userData.lastName) {
      return `${userData.firstName} ${userData.lastName}`;
    }
    if (userData.displayName) {
      return userData.displayName;
    }
    return userData.email?.split('@')[0] || 'Unknown';
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'super_admin': return 'role-badge super-admin';
      case 'club_admin': return 'role-badge club-admin';
      default: return 'role-badge member';
    }
  };

  const filteredAdmins = filter === 'all' 
    ? admins 
    : admins.filter(a => a.role === filter);

  const nonAdminUsers = allUsers.filter(u => 
    u.role !== 'super_admin' && u.role !== 'club_admin'
  );

  if (loading) {
    return (
      <div className="admin-management-page">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="admin-management-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>This page is only accessible to Super Admins.</p>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-management-page">
      <div className="admin-management-header">
        <div className="header-left">
          <Link to="/super-admin" className="back-link">← Back to Dashboard</Link>
          <h1>Admin Management</h1>
          <p>Manage platform and club administrators</p>
        </div>
        <div className="admin-stats">
          <div className="stat-box">
            <span className="stat-number">{admins.filter(a => a.role === 'super_admin').length}</span>
            <span className="stat-label">Super Admins</span>
          </div>
          <div className="stat-box">
            <span className="stat-number">{admins.filter(a => a.role === 'club_admin').length}</span>
            <span className="stat-label">Club Admins</span>
          </div>
          <div className="stat-box">
            <span className="stat-number">{allUsers.length}</span>
            <span className="stat-label">Total Users</span>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button 
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Admins ({admins.length})
        </button>
        <button 
          className={`filter-tab ${filter === 'super_admin' ? 'active' : ''}`}
          onClick={() => setFilter('super_admin')}
        >
          Super Admins ({admins.filter(a => a.role === 'super_admin').length})
        </button>
        <button 
          className={`filter-tab ${filter === 'club_admin' ? 'active' : ''}`}
          onClick={() => setFilter('club_admin')}
        >
          Club Admins ({admins.filter(a => a.role === 'club_admin').length})
        </button>
      </div>

      {/* Admins Table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Club</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAdmins.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">No admins found</td>
              </tr>
            ) : (
              filteredAdmins.map(admin => (
                <tr key={admin.id} className={admin.id === user.uid ? 'current-user' : ''}>
                  <td>
                    <div className="user-name">
                      {getDisplayName(admin)}
                      {admin.id === user.uid && <span className="you-badge">(You)</span>}
                    </div>
                  </td>
                  <td>{admin.email}</td>
                  <td>
                    <span className={getRoleBadgeClass(admin.role)}>
                      {admin.role === 'super_admin' ? 'Super Admin' : 'Club Admin'}
                    </span>
                  </td>
                  <td>
                    {admin.clubId ? (
                      <span className="club-name">{clubs[admin.clubId]?.name || admin.clubId}</span>
                    ) : (
                      <span className="no-club">No club</span>
                    )}
                  </td>
                  <td>
                    {admin.id !== user.uid ? (
                      <div className="action-buttons">
                        {admin.role === 'club_admin' && (
                          <button 
                            className="btn-small btn-promote"
                            onClick={() => updateUserRole(admin.id, 'super_admin')}
                          >
                            Promote to Super
                          </button>
                        )}
                        {admin.role === 'super_admin' && (
                          <button 
                            className="btn-small btn-demote"
                            onClick={() => updateUserRole(admin.id, 'club_admin')}
                          >
                            Demote to Club
                          </button>
                        )}
                        <button 
                          className="btn-small btn-remove"
                          onClick={() => updateUserRole(admin.id, 'member')}
                        >
                          Remove Admin
                        </button>
                        <select 
                          className="club-select"
                          value={admin.clubId || ''}
                          onChange={(e) => assignToClub(admin.id, e.target.value)}
                        >
                          <option value="">No Club</option>
                          {Object.entries(clubs).map(([id, club]) => (
                            <option key={id} value={id}>{club.name}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <span className="no-actions">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Promote Regular Users Section */}
      <div className="promote-section">
        <div className="section-header" onClick={() => setShowAllUsers(!showAllUsers)}>
          <h2>Promote Users to Admin</h2>
          <button className="toggle-btn">
            {showAllUsers ? '▼ Hide' : '▶ Show'} ({nonAdminUsers.length} users)
          </button>
        </div>

        {showAllUsers && (
          <div className="users-table-container">
            {nonAdminUsers.length === 0 ? (
              <p className="no-users">No regular users found. All users are already admins.</p>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Current Role</th>
                    <th>Club</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {nonAdminUsers.map(userData => (
                    <tr key={userData.id}>
                      <td>{getDisplayName(userData)}</td>
                      <td>{userData.email}</td>
                      <td>
                        <span className={getRoleBadgeClass(userData.role)}>
                          {userData.role || 'member'}
                        </span>
                      </td>
                      <td>
                        {userData.clubId ? (
                          <span className="club-name">{clubs[userData.clubId]?.name || userData.clubId}</span>
                        ) : (
                          <span className="no-club">No club</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-small btn-promote"
                            onClick={() => updateUserRole(userData.id, 'club_admin')}
                          >
                            Make Club Admin
                          </button>
                          <button 
                            className="btn-small btn-promote-super"
                            onClick={() => updateUserRole(userData.id, 'super_admin')}
                          >
                            Make Super Admin
                          </button>
                          <select 
                            className="club-select"
                            value={userData.clubId || ''}
                            onChange={(e) => assignToClub(userData.id, e.target.value)}
                          >
                            <option value="">No Club</option>
                            {Object.entries(clubs).map(([id, club]) => (
                              <option key={id} value={id}>{club.name}</option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminManagement;
